import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Using Web3Forms - free email form service
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: '769ee3e5-bcb9-404c-80f1-119b5efd6eb0',
          subject: `New Inquiry from ${formData.name} - Pankaj R Goyal & Associates`,
          from_name: 'Pankaj R Goyal Website',
          name: formData.name,
          phone: formData.phone,
          email: formData.email || 'Not provided',
          message: formData.message,
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success('Message sent successfully! We will get back to you soon.', {
          icon: <CheckCircle className="w-5 h-5 text-green-500" />
        });
        setFormData({ name: '', phone: '', email: '', message: '' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again or contact us directly.', {
        duration: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      content: '+91 9636557944',
      link: 'tel:9636557944'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'pankaj@pankajrgoyal.com',
      link: 'mailto:pankaj@pankajrgoyal.com'
    },
    {
      icon: MapPin,
      title: 'Location',
      content: 'India',
      link: null
    }
  ];

  return (
    <section id="contact" className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-teal-600 font-semibold text-sm uppercase tracking-wide">Contact Us</span>
          <h2 className="text-4xl font-bold text-slate-900 mt-3 mb-4">
            Get in Touch
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Have questions or need assistance? Reach out to discuss how we can help you achieve your financial goals.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Contact Information</h3>
              <div className="space-y-6">
                {contactInfo.map((info, index) => {
                  const IconComponent = info.icon;
                  return (
                    <div key={index} className="flex items-start gap-4">
                      <div className="bg-teal-100 p-3 rounded-lg flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-teal-700" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 font-medium mb-1">{info.title}</p>
                        {info.link ? (
                          <a
                            href={info.link}
                            className="text-lg text-slate-900 hover:text-teal-600 transition-colors font-medium"
                          >
                            {info.content}
                          </a>
                        ) : (
                          <p className="text-lg text-slate-900 font-medium">{info.content}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Additional Info Card */}
            <div className="bg-gradient-to-br from-teal-600 to-slate-800 rounded-2xl p-8 text-white shadow-lg">
              <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
              <p className="text-teal-100 mb-6 leading-relaxed">
                Let's discuss your financial needs and explore how our professional services can add value to your business or personal finances.
              </p>
              <div className="flex items-center gap-3 text-teal-100">
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                <span>Free initial consultation</span>
              </div>
              <div className="flex items-center gap-3 text-teal-100 mt-3">
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                <span>Quick response within 24 hours</span>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Send a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                  Full Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                  Phone Number *
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                  Your Message *
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your requirements..."
                  rows={5}
                  className="w-full resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white text-lg py-6"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    Send Message
                    <Send className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
